import { useConversationMediaContext } from '@modules/conversation/providers/ConversationMedia';
import { useMessageMediaContent } from '@modules/conversation/state/ConversationMedia';
import { Typography, styled } from '@mui/material';
import cn from 'classnames';
import { ConversationMessageContentType } from 'src/@types/conversation';
import { CircularProgressWithLabel } from 'src/components/CircularProgressWithLabel';
import Iconify from 'src/components/iconify';
import Image from 'src/components/image';
import { useLocales } from 'src/locales';
import { IConversationMessageContentProps } from './types';

export function ConversationMessageContentImage({
  message,
  compact,
  isReply,
}: IConversationMessageContentProps) {
  const type =
    message.contentType === ConversationMessageContentType.VIDEO
      ? 'mediaThumbnailRef'
      : 'mediaRef';

  const { mediaContent } = useMessageMediaContent(message, type);
  const { setOpenMessageMedia } = useConversationMediaContext();
  const { translate } = useLocales();

  const handleOpenMedia = () => {
    setOpenMessageMedia(message);
  };

  return (
    <ImageContainer className={cn({ isReply })}>
      {!mediaContent || mediaContent?.percentageLoaded ? (
        <div className="loading__container">
          <CircularProgressWithLabel
            value={mediaContent?.percentageLoaded || 1}
          />
          <Typography variant="overline" color="textSecondary">
            {translate('shared.loading.loading_image')}
          </Typography>
        </div>
      ) : (
        <>
          {type === 'mediaThumbnailRef' && (
            <div className="play-icon" onClick={handleOpenMedia}>
              <Iconify
                icon="tabler:player-play-filled"
                width={32}
                height={32}
              />
            </div>
          )}
          <Image
            className={cn('image', { compact })}
            disabledEffect
            alt="attachment"
            src={mediaContent?.mediaContent}
            onClick={() => setOpenMessageMedia(message)}
          />
        </>
      )}
    </ImageContainer>
  );
}

const ImageContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  position: 'relative',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: 350,
  height: 350,
  border: `solid 1px ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  gap: 1,
  marginTop: theme.spacing(1),

  '& .loading__container': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: 350,
    height: 350,
    border: `solid 1px ${theme.palette.divider}`,
    gap: theme.spacing(1),
  },

  '& .play-icon': {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1),
    borderRadius: '100%',
    width: 40,
    height: 40,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    cursor: 'pointer',
    opacity: 0.75,
    transition: 'all linear .1s',
    zIndex: 1,
  },

  '& .image': {
    width: 350,
    height: 350,
    cursor: 'pointer',
    borderRadius: theme.shape.borderRadius,

    '&.compact': {
      width: 48,
      height: 48,
    },
  },

  '&:hover': {
    '.play-icon': {
      transform: 'scale(1.2)',
      opacity: 1,
    },
  },

  '&.isReply': {
    width: 125,
    height: 125,
    marginTop: theme.spacing(0.5),

    '& .loading__container, & .image': {
      width: 125,
      height: 125,
    },

    '& .play-icon': {
      padding: theme.spacing(0.5),
      width: 20,
      height: 20,
    },
  },
}));
